/** Utility functions with legacy browser fallbacks */
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { hasRenderer } from '../registry/renderers';
export function now() {
    return typeof performance !== 'undefined' && performance.now
        ? performance.now()
        : Date.now();
}
export function requestFrame(callback) {
    if (typeof requestAnimationFrame !== 'undefined') {
        return requestAnimationFrame(callback);
    }
    return setTimeout(function () { return callback(now()); }, 16);
}
export function cancelFrame(id) {
    if (typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(id);
    }
    else {
        clearTimeout(id);
    }
}
export function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
export function lerp(a, b, t) {
    return a + (b - a) * t;
}
export function degToRad(deg) {
    return (deg * Math.PI) / 180;
}
export function radToDeg(rad) {
    return (rad * 180) / Math.PI;
}
export function uid(prefix) {
    if (prefix === void 0) { prefix = 'ld'; }
    return prefix + '_' + Math.random().toString(36).slice(2, 11);
}
export { tryParseColor, parseColor, colorWithAlpha, mixColors, interpolateColor, isCssColorString, } from './color';
export function resolveContainer(container) {
    if (typeof container === 'string') {
        var el = document.querySelector(container);
        if (!el)
            throw new Error("LightDraw: container \"".concat(container, "\" not found"));
        return el;
    }
    return container;
}
export function getPixelRatio() {
    return typeof window !== 'undefined' && window.devicePixelRatio
        ? window.devicePixelRatio
        : 1;
}
export function merge(target) {
    var e_1, _a;
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    try {
        for (var sources_1 = __values(sources), sources_1_1 = sources_1.next(); !sources_1_1.done; sources_1_1 = sources_1.next()) {
            var source = sources_1_1.value;
            if (!source)
                continue;
            for (var key in source) {
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (sources_1_1 && !sources_1_1.done && (_a = sources_1.return)) _a.call(sources_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return target;
}
export function shallowEqual(a, b) {
    var e_2, _a;
    var keysA = Object.keys(a);
    var keysB = Object.keys(b);
    if (keysA.length !== keysB.length)
        return false;
    try {
        for (var keysA_1 = __values(keysA), keysA_1_1 = keysA_1.next(); !keysA_1_1.done; keysA_1_1 = keysA_1.next()) {
            var key = keysA_1_1.value;
            if (a[key] !== b[key])
                return false;
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (keysA_1_1 && !keysA_1_1.done && (_a = keysA_1.return)) _a.call(keysA_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return true;
}
/** Object pool for reducing GC pressure */
var ObjectPool = /** @class */ (function () {
    function ObjectPool(factory, reset, initialSize) {
        if (initialSize === void 0) { initialSize = 16; }
        this.pool = [];
        this.factory = factory;
        this.reset = reset;
        for (var i = 0; i < initialSize; i++) {
            this.pool.push(factory());
        }
    }
    ObjectPool.prototype.acquire = function () {
        var _a;
        return (_a = this.pool.pop()) !== null && _a !== void 0 ? _a : this.factory();
    };
    ObjectPool.prototype.release = function (obj) {
        this.reset(obj);
        this.pool.push(obj);
    };
    return ObjectPool;
}());
export { ObjectPool };
/** 2D matrix for transforms */
var Matrix2D = /** @class */ (function () {
    function Matrix2D() {
        this.a = 1;
        this.b = 0;
        this.c = 0;
        this.d = 1;
        this.e = 0;
        this.f = 0;
    }
    Matrix2D.prototype.identity = function () {
        this.a = 1;
        this.b = 0;
        this.c = 0;
        this.d = 1;
        this.e = 0;
        this.f = 0;
        return this;
    };
    Matrix2D.prototype.translate = function (x, y) {
        this.e += this.a * x + this.c * y;
        this.f += this.b * x + this.d * y;
        return this;
    };
    Matrix2D.prototype.scale = function (sx, sy) {
        this.a *= sx;
        this.b *= sx;
        this.c *= sy;
        this.d *= sy;
        return this;
    };
    Matrix2D.prototype.rotate = function (angleRad) {
        var cos = Math.cos(angleRad);
        var sin = Math.sin(angleRad);
        var _a = this, a = _a.a, b = _a.b, c = _a.c, d = _a.d;
        this.a = a * cos + c * sin;
        this.b = b * cos + d * sin;
        this.c = c * cos - a * sin;
        this.d = d * cos - b * sin;
        return this;
    };
    Matrix2D.prototype.skew = function (skewX, skewY) {
        var tanX = Math.tan(skewX);
        var tanY = Math.tan(skewY);
        var _a = this, a = _a.a, b = _a.b, c = _a.c, d = _a.d;
        this.a = a + c * tanY;
        this.b = b + d * tanY;
        this.c = c + a * tanX;
        this.d = d + b * tanX;
        return this;
    };
    Matrix2D.prototype.multiply = function (other) {
        var _a = this, a = _a.a, b = _a.b, c = _a.c, d = _a.d, e = _a.e, f = _a.f;
        this.a = a * other.a + c * other.b;
        this.b = b * other.a + d * other.b;
        this.c = a * other.c + c * other.d;
        this.d = b * other.c + d * other.d;
        this.e = a * other.e + c * other.f + e;
        this.f = b * other.e + d * other.f + f;
        return this;
    };
    Matrix2D.prototype.transformPoint = function (x, y) {
        return {
            x: this.a * x + this.c * y + this.e,
            y: this.b * x + this.d * y + this.f,
        };
    };
    Matrix2D.prototype.invert = function () {
        var inv = new Matrix2D();
        return this.invertInto(inv);
    };
    /** Invert into an existing matrix (avoids allocation when paired with pool). */
    Matrix2D.prototype.invertInto = function (out) {
        var det = this.a * this.d - this.b * this.c;
        if (Math.abs(det) < 1e-10)
            return null;
        out.a = this.d / det;
        out.b = -this.b / det;
        out.c = -this.c / det;
        out.d = this.a / det;
        out.e = (this.c * this.f - this.d * this.e) / det;
        out.f = (this.b * this.e - this.a * this.f) / det;
        return out;
    };
    Matrix2D.prototype.copyFrom = function (other) {
        this.a = other.a;
        this.b = other.b;
        this.c = other.c;
        this.d = other.d;
        this.e = other.e;
        this.f = other.f;
        return this;
    };
    Matrix2D.prototype.toCSS = function () {
        return "matrix(".concat(this.a, ",").concat(this.b, ",").concat(this.c, ",").concat(this.d, ",").concat(this.e, ",").concat(this.f, ")");
    };
    return Matrix2D;
}());
export { Matrix2D };
/** Shared matrix pool for hit testing hot paths */
export var matrixPool = new ObjectPool(function () { return new Matrix2D(); }, function (m) { return m.identity(); });
export function detectBestRenderer() {
    if (typeof document === 'undefined')
        return 'canvas';
    try {
        var canvas = document.createElement('canvas');
        if (canvas.getContext && canvas.getContext('2d') && hasRenderer('canvas'))
            return 'canvas';
    }
    catch (_a) {
        /* jsdom and some embedded environments lack canvas */
    }
    if (typeof SVGSVGElement !== 'undefined' && hasRenderer('svg'))
        return 'svg';
    if (hasRenderer('html'))
        return 'html';
    return 'canvas';
}
